import { IRequest, IShopifyLineItem, IShopifyLineItemProperty } from "../types/eventTypes";

/** Line-item properties that are logistics/internal, not customer menu selections. */
const EXCLUDED_PROPERTY_NAMES = new Set([
	"delivery date",
	"delivery time",
	"event date",
	"event day",
	"event start time",
]);

export interface IPrepaidMenuSelection {
	name: string;
	value: string;
}

export interface IPrepaidMenuPackage {
	name: string;
	selections: IPrepaidMenuSelection[];
}

const isMenuSelectionProperty = (property?: IShopifyLineItemProperty | null): boolean => {
	if (!property?.name) {
		return false;
	}

	const name = property.name.trim();
	if (!name || name.startsWith("_")) {
		return false;
	}

	if (EXCLUDED_PROPERTY_NAMES.has(name.toLowerCase())) {
		return false;
	}

	return property.value != null && String(property.value).trim() !== "";
};

const getSelectionsForLineItem = (lineItem: IShopifyLineItem): IPrepaidMenuSelection[] => {
	return (lineItem.properties || [])
		.filter(isMenuSelectionProperty)
		.map((property) => ({
			name: property.name.trim(),
			value: String(property.value).trim(),
		}));
};

/**
 * Returns Globo customer menu selections grouped by package, or null when none exist.
 */
export const getPrepaidMenuSelectionPackages = (
	request?: IRequest | null,
): IPrepaidMenuPackage[] | null => {
	const lineItems = request?.payload?.line_items;
	if (!Array.isArray(lineItems) || lineItems.length === 0) {
		return null;
	}

	const packagesWithSelections = lineItems
		.map((lineItem) => {
			const selections = getSelectionsForLineItem(lineItem);
			if (!selections.length) {
				return null;
			}

			return {
				name: (lineItem.name || lineItem.title || "").trim(),
				selections,
			};
		})
		.filter((item): item is IPrepaidMenuPackage => !!item);

	return packagesWithSelections.length > 0 ? packagesWithSelections : null;
};

/**
 * Prefer Globo customer menu selections from Shopify line item properties.
 * Fall back to the complete package menu when no selections exist.
 */
export const getPrepaidMenuDisplayText = (request?: IRequest | null): string => {
	if (!request) {
		return "";
	}

	const packagesWithSelections = getPrepaidMenuSelectionPackages(request);
	if (packagesWithSelections?.length === 1) {
		return packagesWithSelections[0].selections
			.map((selection) => `${selection.name}: ${selection.value}`)
			.join("\n");
	}

	if (packagesWithSelections && packagesWithSelections.length > 1) {
		return packagesWithSelections
			.map(({ name, selections }) => {
				const selectionLines = selections
					.map((selection) => `• ${selection.name}: ${selection.value}`)
					.join("\n");
				return name ? `${name}\n${selectionLines}` : selectionLines;
			})
			.join("\n\n");
	}

	return request.menu || "";
};
